import React from 'react';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';
import DOMPurify from 'dompurify';
import { reportError } from 'telemetry';
import {
  ColumnDataTypes,
  ColumnModes,
  DimensionMode,
  DimensionNoValue,
  DimensionNullValue,
  DimensionNumericNoValue,
  DimensionStatus,
  draggableItemType,
  EntitiesTypes,
  ExportStatus,
  FieldEntitiesType,
  GroupedSubTotalsSeperator,
  MAX_COLUMN_DESCRIPTION_CHARACTER_LIMIT,
  MAX_DESC_CHARACTER_LIMIT,
  MAX_NAME_CHARACTER_LIMIT,
  MIN_CHARACTER_LIMIT,
  PreDefinedFieldEntityType,
  ProductLinesTypes,
  QueryParamNames,
  SubTotalsSeperator,
  ToolbarDisplayMenuItems,
  ToolbarMenuItemTypes,
  ZeroAnchorStatus,
} from 'core/constants/report';
import { DateBinningFormatters, ZERO_TO_EMPTY_STRING_BINNING_TYPES } from 'core/constants/formatters';
import {
  FilterModel,
  GridModel, ObjModel, ReportReduxModel, ReportResponseModel, ReportToolbarModel,
} from 'core/models';
import FilterIcon from 'components/common/svg/Filter/filter.svg';
import {
  displayName, isValidArray, isValidArrayAndNotEmpty, isValidObjectAndNotEmpty,
} from 'components/feature/Report/ReportSidebar/common/helpers';
import LeadIcon from 'components/common/svg/Lead/lead.svg';
import UserIcon from 'components/common/svg/User/User.svg';
import ActivityIcon from 'components/common/svg/Activity/activity.svg';
import TaskIcon from 'components/common/svg/Task/task.svg';
import MavisIcon from 'components/common/svg/Mavis/mavis.svg';
import { restrictedCharsForInput, restrictedCharsForInputForEmail, userStatusEnum } from 'components/feature/Report/ReportSidebar/common/constants';
import FailedIcon from 'components/common/svg/Failed/failed.svg';
import SuccessIcon from 'components/common/svg/Success/success.svg';
import LoadingComponent from 'components/common/LoadingSpinner/loading-component';
import PreDefinedIcon from 'components/common/svg/PreDefined/pre-defined.svg';
import OpportunityIcon from 'components/common/svg/Opportunity/opportunity.svg';
import ChartsIcon from 'components/common/svg/ChartIconSidebar/chart-icon-sidebar.svg';
import { ChartAttributesKey, VisualizationTypes } from 'core/constants/visualizations';
import { EntityList, IChartAttributes, IDimension } from 'core/models/report-redux';
import {
  IAttributesOptions,
  IChartAttributesKeys,
  IChartLabels,
} from 'components/feature/Report/ReportSidebar/ChartsVisualization/chart-visualization.type';
import {
  IColumn, IColumnDimension, IFilterConfig, IFiltersMetaData, IDimensionMetaField, ICustomFilterDefinition,
} from 'core/models/report-response';
import {
  IBuilder,
  IBuilderMeasure,
  ICustomHierarchy,
  IDimensionsMetaFields,
  IPaging,
  IReportBuilderColumn,
  IReportConfig,
  ISorting,
  ReportSourceInfo,
  ReportType,
  SortingOrderTypes,
} from 'core/models/report-builder/report-builder';
import { columnSortMap, ISortingRanked, makeColumnSortMap } from 'core/utils/sorting';
import {
  AccountsActivityEntityIcon,
  AccountsEntityIcon, CustomEntityIcon, CustomExpressionIcon, DataSettingHighlightedIcon, MetaDataIcon, PinnedIconNew, TicketEntity,
  WebContentEntityIcon, SystemDataIcon,
  DistributionEngineIcon,
  FieldSalesIcon,
  UnionTableIcon,
} from 'components/common/svg';
import { IPanelVisualisation } from 'core/models/dashboard';
import { PendoClassNames } from 'components/feature/Report/ReportSidebar/common/pendoClassNames';
import useLocalize from 'components/common/Localization/useLocalize.hook';
import { CUSTOM_HIERARCHY_FILTER_SUFFIX, drilldownColumnDelimiter, FeatureContext } from 'core/constants/common';
import { FilterType, LinkedFilterSource } from 'core/constants/filter';
import { ReportFetchCriteria, SelectedRow } from 'services/report.service';
import { IReportState } from 'redux-v2/report/report-store.state';
import {
  getColumnsForActiveDimensions, getUpdatedMeasures, processEntityGroupDimensions, removeBuilderPropForMeasure,
} from 'redux-v2/report/report-store.utils';
import {
  getFilterMetaDataInfo, getSavePayloadBuilderDimensions, getSavePayloadBuilderFilters, getSavePayloadDimensionColumns, getSavePayloadMeasureColumns, getSavePayloadBuilderMeasures,
  getDefaultValue,
} from 'redux-v2/report-builder/report-builder-store.utils';
import TicketActivityEntity from 'components/common/svg/TicketActivityEntity/ticket-activity-entity.svg';
import { IColumnConfig } from 'core/models/grid';
import { usersPrefix } from 'core/constants/report-expressions';
import {
  DrilldownConfigMapping,
  IConditionalFormatting,
  ILinkedConfig,
  ReportType as ReportBuilderType,
} from 'redux-v2/report-builder/report-builder-store.state';
import SalesCloudIcon from 'components/common/svg/SalesCloud/sales-cloud.svg';
import ConverseIcon from 'components/common/svg/Converse/converse.svg';
import LandingPageProIcon from 'components/common/svg/LandingPagePro/landing-page-pro.svg';
import ServiceCloudIcon from 'components/common/svg/ServiceCloud/service-cloud.svg';
import {
  IExpression, isAgg, isGroupConcat, isGroupConcatDistinct,
} from 'core/models/report-expressions';
import LeadSystemAuditIcon from 'components/common/svg/LeadSystemAudit/lead-system-audit-icon.svg';
import QueryIcon from 'components/common/svg/QueryIcon/query-icon.svg';
import uniqBy from 'lodash/uniqBy';
import { SIERAPage } from 'core/constants/page-url';
import ConnectorIcon from 'components/common/svg/Connector/connector.svg';
import AutomationIcon from 'components/common/svg/Automation/automation.svg';
import ConverseDataSourceIcon from 'components/common/svg/Converse/conversedatasource.svg';
import { getColumnsProperties, isListReport } from './report-builder.util';
import { FilledReportPinnedIcon } from './custom-icons';
import { globalLogger } from './GlobalLogger';
import {
  applyValueToConstituent,
  clearPrefix,
  isLargeOptionSetFilter,
} from './common.utility';
import { getBrokenCustomFilterExpressions, IExpressionStoreLike } from './report-expressions';
import { getMetaFieldId, isColumnUserHidden } from './entity-grouping.util';
import { isNumericMeasure } from './chart.utility';

export const ReportToolbarMenuOptions = [
  {
    id: ToolbarMenuItemTypes.Filters,
    name: ToolbarMenuItemTypes.Filters,
    icon: FilterIcon,
    panelIcon: FilterIcon,
    desc: 'cmn.filterDesc',
    className: PendoClassNames.Filters,
    displayName: ToolbarDisplayMenuItems.Filters,
  },
  {
    id: ToolbarMenuItemTypes.Setting,
    name: ToolbarMenuItemTypes.Setting,
    icon: DataSettingHighlightedIcon,
    panelIcon: DataSettingHighlightedIcon,
    desc: 'cmn.settingsDesc',
    className: PendoClassNames.Setting,
    displayName: ToolbarDisplayMenuItems.Setting,
  },
  {
    id: ToolbarMenuItemTypes.Charts,
    name: ToolbarMenuItemTypes.Charts,
    icon: ChartsIcon,
    panelIcon: ChartsIcon,
    desc: 'cmn.visualiseDesc',
    className: PendoClassNames.ChartSideBar,
    displayName: ToolbarDisplayMenuItems.Charts,
  },
  {
    id: ToolbarMenuItemTypes.PinnedReports,
    name: ToolbarMenuItemTypes.PinnedReports,
    icon: PinnedIconNew,
    panelIcon: FilledReportPinnedIcon,
    desc: 'cmn.pinnedRepDesc',
    className: PendoClassNames.PinnedReports,
    displayName: ToolbarDisplayMenuItems.PinnedReports,
  },
] as Array<ReportToolbarModel.IToolbarMenuItem>;

export const getTextTooltipContentForDimension = (dimension: ReportReduxModel.IDimension | IColumn) => {
  if (dimension) {
    const text = displayName(dimension);
    if (dimension?.Props?.Namespace || dimension?.BuilderConfig?.Alias) {
      return `${dimension?.Props?.Namespace ?? ''}  ${dimension?.BuilderConfig?.Alias ? `| ${dimension?.BuilderConfig?.Alias}` : ''} `;
    }
    return text;
  }
  return '';
};

export const getFieldIdentifier = (
  field: ReportResponseModel.IColumn | ReportReduxModel.IDimension,
) => {
  if (field) {
    if (isMeasure(field as IColumn) && field.BuilderConfig?.Entity === FieldEntitiesType.Custom) {
      return field.BuilderConfig.uniqueKey?.toString();
    }
    if (isMeasureDynamic(field as IColumn)) {
      return field.Name + field?.BuilderConfig?.Alias || '';
    }
    if (isDimensionCustom(field as IColumn)) {
      return field.Name;
    }
    return getEntityKey(field);
  }
  return null;
};

export const getFieldName = (field: ReportResponseModel.IColumn | ReportReduxModel.IDimension) => {
  if (field?.BuilderConfig?.Entity === FieldEntitiesType.Custom || isMeasureDynamic(field as IColumn)) {
    // reuse getFieldIdentifier code for these conditions
    return getFieldIdentifier(field);
  }
  return field.Name;
};

export const getFieldDisplayName = (field: ReportResponseModel.IColumn, isDrilldown: boolean = false) => {
  let name;
  if (field) {
    if (field.BuilderConfig && field.BuilderConfig.IsDynamicField) {
      return `${field.BuilderConfig.DisplayName}`;
    }
    name = field.Props.ReferTo || field.Name || '';
  }
  if (isDrilldown) {
    name = clearPrefix(name, drilldownColumnDelimiter);
  }
  return name;
};

export const getTextTooltipContentForField = (field: ReportResponseModel.IColumn) => {
  if (field) {
    const text = getFieldDisplayName(field);
    if (field?.Props?.Namespace || field?.BuilderConfig?.Alias) {
      return `${field?.Props?.Namespace ?? ''}  ${field?.BuilderConfig?.Alias ? `| ${field.BuilderConfig.Alias}` : ''} `;
    }
    return text;
  }
  return '';
};

export const isMeasure = (column: IColumn) => !!column?.Props?.Measure?.IsMeasure;
export const isDimension = (column: IColumn) => !!column?.Props?.Dimension;
export const isMeasureAndApplied = (column: IColumn) => isMeasure(column) && column.Props.Mode !== ColumnModes.Hidden && column.Props.Mode !== ColumnModes.NotApplied;
export const isDimensionColumnApplied = (column: IColumn) => isValidObjectAndNotEmpty(column) && column.Props.Mode !== ColumnModes.Hidden && column.Props.Mode !== ColumnModes.NotApplied;
export const isMeasureDynamic = (column: IColumn) => !!column?.Props?.Measure?.Expression;
export const isMeasurePredefined = (column: IColumn) => isMeasure(column) && !isMeasureDynamic(column) && !isMeasureCustom(column);
export const isMeasureCustom = (column: IColumn) => isMeasure(column) && column.BuilderConfig?.Entity === FieldEntitiesType.Custom;
export const isDimensionCustom = (column: IColumn) => column?.Props?.Dimension && column?.BuilderConfig?.Entity === FieldEntitiesType.Custom;
export const getAppliedMeasures = (columns: IColumn[]) => columns?.filter((c) => isMeasureAndApplied(c));
export const isCustomHierarchyColumnOrDimension = (column: IColumn | IDimension) => column?.Props?.CustomHierarchyId;
export const isDateTypeOfField = (column: IColumn | IDimension | IColumnDimension) => (column as IColumn | IDimension)?.BuilderConfig?.DataType === ColumnDataTypes.DateTime || (column as IColumn | IDimension)?.Props?.Dimension?.DimensionProp?.DataType === ColumnDataTypes.DateTime;
export const isMaxMeasuresApplied = (columns: IColumn[], maxAppliedMeasures?: number) => columns?.length === maxAppliedMeasures;
export const isWrongInput = (inputText: string) => !(DOMPurify.sanitize(inputText) === inputText) || restrictedCharsForInput.some((char: string) => (!!inputText.includes(char)));
export const isWrongInputForEmail = (inputText: string) => !(DOMPurify.sanitize(inputText) === inputText) || restrictedCharsForInputForEmail.some((char: string) => (!!inputText.includes(char)));
export const isInputLimitReached = (inputText: string, limit: number) => inputText.length >= limit;
export const getColumnDescriptionErrorKey = (value: string): string | null => {
  if (!value) return null;
  if (isWrongInput(value)) return 'cmn.colDescRestrictedCharsError';
  if (value.length > MAX_COLUMN_DESCRIPTION_CHARACTER_LIMIT) return 'cmn.colDescCharLimitError';
  return null;
};
export const isEmptyValue = (value: any) => value === undefined || value === null || value === '';

export const isDimensionApplied = (dim: IColumnDimension) => !!dim && dim.Applied !== DimensionStatus.NotApplied;
export const isDimensionRow = (dim: IColumnDimension) => dim?.DimensionMode === DimensionMode.RowGroup || dim?.DimensionMode === DimensionMode.Both;
export const isDimensionColumn = (dim: IColumnDimension) => dim?.DimensionMode === DimensionMode.ColumnGroup || dim?.DimensionMode === DimensionMode.Both;
// For Dynamic having no metafield we pass the Name
export const referredName = (col: IColumn | IDimension, columns: Array<IColumn | IDimension>) => (((col as IDimension).ReferTo && (columns as IDimension[]).find((dim: IDimension) => col.Props.ReferTo === dim.Id)?.Name) || col.Props?.ReferTo || col.Name);

export const isPieOrDonutChart = (type: VisualizationTypes) => type === VisualizationTypes.PieChart || type === VisualizationTypes.DonutChart;
export const isReportJoined = (report: IReportConfig) => report?.Builder?.some((b) => !!b.Joins?.length);

/** Checks whether a dimension is additional/dynamic and not from the report config. */
export const isDimensionDynamic = (dim: IDimension | IColumn) => !!dim?.BuilderConfig?.IsDynamicField;
/** Generates a unique string for the given dimension composed of its join ID, alias, and/or name. */
export const makeDimensionKey = (dim: IDimension | IColumn) => `${dim?.BuilderConfig?.JoinID ? `${dim?.BuilderConfig?.JoinID}:` : ''}${dim?.Id ?? ''}`;
/** Compares two dimensions or columns. */
export const areDimensionsEqual = (dim1: IDimension | IColumn, dim2: IDimension | IColumn) => {
  const oneIsDynamic = isDimensionDynamic(dim1);
  const twoIsDynamic = isDimensionDynamic(dim2);
  if ((oneIsDynamic && !twoIsDynamic) || (!oneIsDynamic && twoIsDynamic)) return false;
  if (isCustomHierarchyColumnOrDimension(dim1) && isCustomHierarchyColumnOrDimension(dim2)) return dim1.Props?.CustomHierarchyId === dim2.Props?.CustomHierarchyId;
  return getEntityKey(dim1) === getEntityKey(dim2);
};
// Check wheater a column is linked to any parent column
export const isColumnReferredCol = (column: ReportResponseModel.IColumn) => !isEmpty(column?.Props.ParentDimension);

/** Generates a unique string for the given dimension composed of its Entity, join ID, alias, and/or name. */
export const getEntityKey = (dim: IDimension | IColumn) => `${dim?.BuilderConfig?.Entity}:${makeDimensionKey(dim)}`;

export const isDefaultUserField = (dim:IDimension | IColumn, linkedFilter: IFilterConfig) => {
  const hasValidLinkedFilter = linkedFilter?.Alias === undefined || linkedFilter.Alias === dim?.BuilderConfig?.SchemaName;
  return !!(
    !dim?.Props?.IsUserDynamicField
    && !dim?.Props?.IsAdditional
    && dim?.Props?.ReferTo
    && hasValidLinkedFilter
  );
};

export const referredDimId = (col: IColumn | IDimension) => (col as IDimension)?.ReferTo || col?.Props?.ReferTo || col?.Id;

// generic search compares two string
// params query:- query string
// params searchField:- string to be compared
// returns true if searchField has contains matching query string
// return false if  query does not match and query is empty.
export const genericSearch = (query: string = '', searchField: string = '') => (
  query
    ? searchField.toString().toLowerCase().includes(query.toLowerCase())
    : true);

export const sortAdditionalFields = <T extends ReportReduxModel.IDimension | ReportResponseModel.IColumn>
  (dimensions: T[]) => sortBy(dimensions, (item) => {
    let name = item.Name;

    // if builder config is present, we chose displayname to sort. We trim and do lowercase to make it readable for user
    if (item.BuilderConfig && item.BuilderConfig.IsDynamicField) {
      name = item.BuilderConfig.DisplayName || '';
    }

    // for default columns, we just show it by alias configured in config
    return [name.toLowerCase().replace(/\s/g, '')];
  });

export const FieldEntities = {
  [PreDefinedFieldEntityType]: {
    icon: PreDefinedIcon,
    color: 'var(--purpleColor)',
  },
  [FieldEntitiesType.Lead]: {
    icon: LeadIcon,
    color: 'var(--avtarColor)',
  },
  [FieldEntitiesType.Activity]: {
    icon: ActivityIcon,
    color: 'var(--expandListButtonHoverColor)',
  },
  [FieldEntitiesType.Opportunity]: {
    icon: OpportunityIcon,
    color: 'var(--opportunityColor)',
  },
  [FieldEntitiesType.Task]: {
    icon: TaskIcon,
    color: 'var(--taskColor)',
  },
  [FieldEntitiesType.Mavis]: {
    icon: MavisIcon,
    color: 'var(--mavisColor)',
  },
  [FieldEntitiesType.User]: {
    icon: UserIcon,
    color: 'var(--userColor)',
  },
  [FieldEntitiesType.Custom]: {
    icon: CustomExpressionIcon,
    color: 'var(--customEntiyColor)',
  },
  [FieldEntitiesType.Ticket]: {
    icon: TicketEntity,
    color: 'var(--ticketEntityColor)',
  },
  [FieldEntitiesType.TicketActivity]: {
    icon: TicketActivityEntity,
    color: 'var(--ticketActivityEntity)',
  },
  [FieldEntitiesType.Accounts]: {
    icon: AccountsEntityIcon,
    color: 'var(--accountsEntity)',
  },
  [FieldEntitiesType.AccountsActivity]: {
    icon: AccountsActivityEntityIcon,
    color: 'var(--accountsActivityEntity)',
  },
  [FieldEntitiesType.WebContent]: {
    icon: WebContentEntityIcon,
    color: 'var(--webContentEntity)',
  },
  [FieldEntitiesType.LeadSystemAudit]: {
    icon: LeadSystemAuditIcon,
    color: 'var(--avtarColor)',
  },
  [FieldEntitiesType.Query]: {
    icon: QueryIcon,
    color: 'var(--purpleColor)',
  },
  [FieldEntitiesType.Connector]: {
    icon: ConnectorIcon,
    color: 'var(--connectorColor)',
  },
  [FieldEntitiesType.Automation]: {
    icon: AutomationIcon,
    color: 'var(--automationColor)',
  },
  [FieldEntitiesType.Converse]: {
    icon: ConverseDataSourceIcon,
    color: 'var(--leadsquaredIconBlue)',
  },
  [FieldEntitiesType.Metadata]: {
    icon: MetaDataIcon,
    color: 'var(--metaDataColor)',
  },
  [FieldEntitiesType.System]: {
    icon: SystemDataIcon,
    color: 'var(--systemDataColor)',
  },
  [FieldEntitiesType.DistributionEngine]: {
    icon: DistributionEngineIcon,
    color: 'var(--distributionEngineColor)',
  },
  [FieldEntitiesType.FieldSales]: {
    icon: FieldSalesIcon,
    color: 'var(--fieldSalesColor)',
  },
  [FieldEntitiesType.UnionTable]: {
    icon: UnionTableIcon,
    color: 'var(--purpleColor)',
  },
} as ObjModel.ObjGeneric<IFieldEntity>;

export const ProductLines = {
  [ProductLinesTypes.SalesCloud]: {
    icon: SalesCloudIcon,
    color: 'var(--secondaryColor)',
  },
  [ProductLinesTypes.Converse]: {
    icon: ConverseIcon,
    color: 'var(--secondaryColor)',
  },
  [ProductLinesTypes.LandingPagePro]: {
    icon: LandingPageProIcon,
    color: 'var(--secondaryColor)',
  },
  [ProductLinesTypes.ServiceCloud]: {
    icon: ServiceCloudIcon,
    color: 'var(--secondaryColor)',
  },
} as ObjModel.ObjGeneric<IFieldEntity>;

export interface IFieldEntity {
  icon: React.ElementType;
  color: string;
}

export const NameSpace = {
  users: 'users',
};

export const ExportStatusIcons = {
  [ExportStatus.InProgress]: {
    icon: LoadingComponent,
    expiryText: 'inProgress',
  },
  [ExportStatus.InQueue]: {
    icon: LoadingComponent,
    expiryText: 'inProgress',
  },
  [ExportStatus.Success]: {
    icon: SuccessIcon,
    expiryText: 'expiresIn',
  },
  [ExportStatus.Failed]: {
    icon: FailedIcon,
    expiryText: 'failed',
  },
} as ObjModel.ObjGeneric<IExportStatusIcon>;

export interface IExportStatusIcon {
  icon: React.ElementType
  expiryText: string
}

export const errorHandler = (error: Error) => {
  // eslint-disable-next-line no-console
  console.error(error);
  reportError(error, { source: 'GlobalErrorHandler' });
};

export const getTextTooltipContentForMeasure = (measure: IColumn) => {
  if (measure) {
    const text = displayName(measure);
    if (measure && (measure?.Props?.Namespace || measure?.BuilderConfig?.Alias)) {
      return `${measure?.Props?.Namespace ?? ''}  ${measure?.BuilderConfig?.Alias ? `| ${measure?.BuilderConfig?.Alias}` : ''} `;
    }
    return text;
  }
  return '';
};

const getRowValue = (data: ObjModel.Obj, column: GridModel.IColumnConfig) => (!data[column?.props?.ReferTo] && !data[column?.colId] ? DimensionNoValue : data[column?.colId]);

const getRowData = (columnsWithSubTotals: IColumnConfig[], data: ObjModel.Obj) => (isValidArrayAndNotEmpty(columnsWithSubTotals) ? columnsWithSubTotals.map((column) => {
  // For metafields if both parent and reffered value is empty we set it as NoValue else we take parent value it self
  if (column?.props?.ReferTo) {
    return ({
      Name: column.colId,
      Value: getRowValue(data, column),
    });
  }
  // Check if it is a dimension set the No Value for falsy Value and in case of drilldown columns bypass this logic
  if (!column.props?.isDrilldownColumn && (column.props?.ReferTo || column.props?.Dimension || column.props?.ParentDimension)) {
    return { Name: column.colId, Value: !data[column.colId] ? DimensionNoValue : data[column.colId] };
  }
  return ({
    Name: column.colId,
    Value: data[column.colId],
  });
}) : []);

export const getRawDataConfig = (activeDimensions: Array<ObjModel.Obj>, reportStore: IReportState): {
  rowData: Array<ObjModel.Obj>,
  columns: Array<GridModel.IColumnConfig>,
} => {
  const columns = getColumnsProperties(
    reportStore.columns,
    reportStore.reportConfig.AllowDrilldownOnMaskedDimensions,
  );
  const columnsWithSubTotals = [...columns];
  const appliedRowDims = isValidArrayAndNotEmpty(reportStore?.columns) ? reportStore?.columns.filter((col) => col.Props.Dimension && col.Props.Dimension.Applied === DimensionStatus.Applied && col.Props.Dimension.DimensionMode === DimensionMode.RowGroup) : [];

  columns?.forEach((item) => {
    if (!!item?.props?.Measure?.IsMeasure && item.props.Mode !== ColumnModes.Hidden && item.props.Mode !== ColumnModes.NotApplied) {
      columnsWithSubTotals.push({ ...item, colId: `${item.colId}${SubTotalsSeperator}` });
      if (appliedRowDims?.length > 2) columnsWithSubTotals.push({ ...item, colId: `${item.colId}${GroupedSubTotalsSeperator}` });
    }
  });
  const rowData: Array<ObjModel.Obj> = [];
  if (reportStore.reportData?.Raw?.Data && reportStore.reportData?.Raw?.Data.length) {
    const reportData = reportStore?.reportData?.Raw?.Data;
    for (let i = 0; i < reportData.length; i += 1) {
      const data = reportData[i];
      rowData.push({
        Fields: getRowData(columnsWithSubTotals, data),
      });
    }
  }

  return { rowData, columns };
};
export const getFilterDisplayName = (filter: ReportResponseModel.IFilterConfig) => filter?.Label || '';

export const getTextTooltipContentForFilter = (filter: ReportResponseModel.IFilterConfig) => {
  if (filter) {
    const text = getFilterDisplayName(filter);
    if (filter?.Namespace || filter?.Alias) {
      return `${filter?.Namespace ?? ''}  ${filter?.Alias ? `| ${filter.Alias}` : ''} `;
    }
    return text;
  }
  return '';
};

// List reports store showWrappedHeader at Builder level; Summary reports nest it in Builder.Properties.
// This helper normalizes both shapes so eases any further debuging.
export const resolveWrappedHeader = (
  Builder: ReportResponseModel.IVisualizationBuilder | ReportResponseModel.IChartVisualizationBuilder,
): boolean => Builder?.showWrappedHeader ?? Builder?.Properties?.showWrappedHeader;

export const getDashboardChartAttributes = (type: VisualizationTypes, chartLabels: IChartLabels, selectedPage: String, key: IChartAttributesKeys) => {
  const dashboardChartAttributes : ObjModel.ObjGeneric<IAttributesOptions> = getChartAttributes(type, chartLabels, selectedPage);
  const keys = Object.keys(dashboardChartAttributes);
  keys?.forEach((item) => {
    dashboardChartAttributes[item as VisualizationTypes].Attributes = dashboardChartAttributes[item as VisualizationTypes]?.Attributes?.filter((attribute) => attribute?.key !== key);
  });
  return dashboardChartAttributes;
};

export const getChartAttributes = (type: VisualizationTypes, chartLabels: IChartLabels, selectedPage: String, isRenderedInReport?: boolean): ObjModel.ObjGeneric<IAttributesOptions> => (
  {
    Table: {
      Type: type,
      Attributes: [
        {
          key: 'showSubTotals',
          label: 'chartAttr.showSubTotals',
          PendoClassNames: `${PendoClassNames.ShowSubTotals}-Column-${selectedPage}`,
          hide: false,
        },
        {
          key: 'showSummary',
          label: 'chartAttr.showSummary',
          PendoClassNames: `${PendoClassNames.ShowSummary}-Column-${selectedPage}`,
          hide: false,
        },
        {
          key: 'showWrappedHeader',
          label: 'chartAttr.showWrappedHeader',
          PendoClassNames: `${PendoClassNames.ShowWrappedHeader}-Column-${selectedPage}`,
          hide: false,
        },
      ],
    },
    Column: {
      Type: type,
      Attributes: [
        {
          key: 'showDataLabels',
          label: 'chartAttr.showLabel',
          PendoClassNames: `${PendoClassNames.ShowDataLabels}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'showSharedTooltip',
          label: 'chartAttr.showSharedTooltip',
          PendoClassNames: `${PendoClassNames.SharedTooltip}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'hideLegend',
          label: 'chartAttr.hideLegend',
          PendoClassNames: `${PendoClassNames.HideLegend}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'stacked',
          label: `chartAttr.${chartLabels[type as keyof IChartLabels]}`,
          PendoClassNames: `${PendoClassNames.Stack}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'fullStacked',
          label: 'chartAttr.stackedPercent',
          PendoClassNames: `${PendoClassNames.HundredPercentStack}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'showSubTotals',
          label: 'chartAttr.showSubTotals',
          PendoClassNames: `${PendoClassNames.ShowSubTotals}-Column-${selectedPage}`,
          hide: true,
        },
        {
          key: 'showSummary',
          label: 'chartAttr.showSummary',
          PendoClassNames: `${PendoClassNames.ShowSummary}-Column-${selectedPage}`,
          hide: true,
        },
      ],
    },
    Line: {
      Type: type,
      Attributes: [
        {
          key: 'showDataLabels',
          label: 'chartAttr.showLabel',
          PendoClassNames: `${PendoClassNames.ShowDataLabels}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'showSeriesLabel',
          label: 'chartAttr.showSeriesLabel',
          PendoClassNames: `${PendoClassNames.ShowSeriesLabels}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'showSharedTooltip',
          label: 'chartAttr.showSharedTooltip',
          PendoClassNames: `${PendoClassNames.SharedTooltip}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'hideLegend',
          label: 'chartAttr.hideLegend',
          PendoClassNames: `${PendoClassNames.HideLegend}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'stacked',
          label: `chartAttr.${chartLabels[type as keyof IChartLabels]}`,
          PendoClassNames: `${PendoClassNames.Stack}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'fullStacked',
          label: 'chartAttr.stackedPercent',
          PendoClassNames: `${PendoClassNames.HundredPercentStack}-Column-${selectedPage}`,
          hide: false,

        },
        {
          key: 'showSubTotals',
          label: 'chartAttr.showSubTotals',
          PendoClassNames: `${PendoClassNames.ShowSubTotals}-Column-${selectedPage}`,
          hide: true,
        },
        {
          key: 'showSummary',
          label: 'chartAttr.showSummary',
          PendoClassNames: `${PendoClassNames.ShowSummary}-Column-${selectedPage}`,
          hide: true,
        },
      ],
    },
    Pie: {
      Type: type,
      Attributes: [
        {
          key: 'showCategoryName',
          label: 'chartAttr.showCategoryName',
          PendoClassNames: `${PendoClassNames.ShowCategoryName}-Column-${selectedPage}`,
          hide: false,
        },
        {
          key: 'showDataLabels',
          label: 'chartAttr.showLabel',
          PendoClassNames: `${PendoClassNames.ShowDataLabels}-Pie-${selectedPage}`,
          hide: false,
        },
        {
          key: 'showPercentage',
          label: 'chartAttr.showPer',
          PendoClassNames: `${PendoClassNames.ChartPercent}-Pie-${selectedPage}`,
          hide: false,
        },
        {
          key: 'hideLegend',
          label: 'chartAttr.hideLegend',
          PendoClassNames: `${PendoClassNames.HideLegend}-Column-${selectedPage}`,
          hide: false,
        },
        {
          key: 'showSubTotals',
          label: 'chartAttr.showSubTotals',
          PendoClassNames: `${PendoClassNames.ShowSubTotals}-Column-${selectedPage}`,
          hide: true,
        },
        {
          key: 'showSummary',
          label: 'chartAttr.showSummary',
          PendoClassNames: `${PendoClassNames.ShowSummary}-Column-${selectedPage}`,
          hide: true,
        },
      ],
    },
    Combo: {
      Type: type,
      Attributes: [
        {
          key: 'showDataLabels',
          label: 'chartAttr.showLabel',
          PendoClassNames: `${PendoClassNames.ShowDataLabels}-Combo-${selectedPage}`,
          hide: false,
        },
        {
          key: 'showSharedTooltip',
          label: 'chartAttr.showSharedTooltip',
          PendoClassNames: `${PendoClassNames.SharedTooltip}-Combo-${selectedPage}`,
          hide: false,
        },
        {
          key: 'hideLegend',
          label: 'chartAttr.hideLegend',
          PendoClassNames: `${PendoClassNames.HideLegend}-Combo-${selectedPage}`,
          hide: false,
        },
        {
          key: 'stacked',
          label: 'chartAttr.stacked',
          PendoClassNames: `${PendoClassNames.Stack}-Combo-${selectedPage}`,
          hide: false,
        },
        {
          key: 'fullStacked',
          label: 'chartAttr.stackedPercent',
          PendoClassNames: `${PendoClassNames.HundredPercentStack}-Combo-${selectedPage}`,
          hide: false,
        },
        {
          key: 'showSubTotals',
          label: 'chartAttr.showSubTotals',
          PendoClassNames: `${PendoClassNames.ShowSubTotals}-Combo-${selectedPage}`,
          hide: true,
        },
        {
          key: 'showSummary',
          label: 'chartAttr.showSummary',
          PendoClassNames: `${PendoClassNames.ShowSummary}-Combo-${selectedPage}`,
          hide: true,
        },
      ],
    },
    Funnel: {
      Type: type,
      // We don't display attributes explicitly here for the funnel chart in report builder pages. They are rendered inside the inside the dedicated funnel chart config page separately.
      Attributes: isRenderedInReport ? [] : [
        {
          key: 'showCategoryName',
          label: 'chartAttr.showCategoryName',
          PendoClassNames: `${PendoClassNames.ShowCategoryName}-Funnel-${selectedPage}`,
          hide: false,
        },
        {
          key: 'showDataLabels',
          label: 'chartAttr.showLabel',
          PendoClassNames: `${PendoClassNames.ShowDataLabels}-Funnel-${selectedPage}`,
          hide: false,
        },
        {
          key: 'hideLegend',
          label: 'chartAttr.hideLegend',
          PendoClassNames: `${PendoClassNames.HideLegend}-Funnel-${selectedPage}`,
          hide: false,
        },
      ],
    },
  }
);
export const validateAndAppendCountToName = (measure: IColumn, allMeasures: Array<IColumn>) => {
  const duplicateMeasureDetails = getDuplicateMeasuresDetails(measure, allMeasures);
  let updatedName = duplicateMeasureDetails.updatedName;
  const { sameMeasureNames, sameMeasureCount } = duplicateMeasureDetails;
  if (sameMeasureCount > 0) {
    const counts: number[] = [];
    let largestNum: number = 0;
    sameMeasureNames.forEach((item: IColumn) => counts.push(parseInt(item?.Name?.split(`${updatedName}_`)[1], 10) || 0));
    counts.forEach((element: number) => {
      if (largestNum <= element) {
        largestNum = element;
      }
    });
    const counter = largestNum + 1;
    updatedName = `${updatedName}_${counter}`;
    return updatedName;
  }
  return updatedName;
};

export const getDuplicateMeasuresDetails = (measure: IColumn, allMeasures: Array<IColumn>) => {
  const updatedName = `Sum(${measure.Name})`;
  const sameMeasureNames = allMeasures.filter((item: IColumn) => item.Name.startsWith(updatedName));
  const sameMeasureCount = sameMeasureNames?.length || 0;
  return { updatedName, sameMeasureNames, sameMeasureCount };
};

/** Sorts the dimensions by their applied status. */
export const reOrderDimensions = (appliedDimensions: IDimension[]) => sortBy(appliedDimensions, (dim) => {
  if (dim.DimensionMode !== DimensionMode.ColumnGroup && dim.Applied !== DimensionStatus.NotApplied) {
    return 0; // row grouped dimensions come first
  }
  if (dim.DimensionMode !== DimensionMode.RowGroup && dim.Applied !== DimensionStatus.NotApplied) {
    return 1; // then column grouped dimensions
  }
  return 2; // finally un-applied and any leftover dimensions
});

export const reOrderMeasures = (measures: IColumn[]): IColumn[] => sortBy(
  measures,
  (m) => (isMeasureAndApplied(m) ? 0 : 1),
);

export const columnsToDimensions = (columns: IColumn[]): IDimension[] => {
  // logic to remove duplicate columns which has same Id's
  const seen = new Set();
  return columns
    ?.map((item) => {
      if (item.Props.Dimension) {
        const key = item.Id;
        if (!seen.has(key)) {
          seen.add(key);
          const dimension = {
            Name: item.Name,
            ReferTo: item.Props.ReferTo,
            ...item.Props.Dimension,
            BuilderConfig: item.BuilderConfig,
            IsMasked: item.Props.IsMasked,
            Mode: item.Props?.Mode,
            IsUserDynamicField: item?.Props?.IsUserDynamicField,
            Props: item?.Props,
            Id: item.Id,
          };
          if (dimension.ZeroAnchor !== ZeroAnchorStatus.Disabled) {
            dimension.ZeroAnchor = ZeroAnchorStatus.Enabled; // un-apply zero values by default
          }
          return dimension;
        }
      }
      return null;
    })
    .filter((item) => !!item);
};

export const sortColumns = (
  sortState: ISorting[], appliedDimensions: IDimension[], appliedMeasures: IColumn[],
  columnType: draggableItemType,
  columnName: string,
  order: SortingOrderTypes | null, // null implies removal of sort
  multiSort: boolean,
  Id: string,
  drilldownFields?: IColumn[],
  isDrilldownPage : boolean = false, // defaults to false, tells if a page is drilldown report or not
  isReportListType?: boolean,
  customHierarchyColumns?: IColumn[],
): [ISorting[], boolean] => {
  let updatedAppliedDimensions = appliedDimensions;
  if (isValidArrayAndNotEmpty(customHierarchyColumns)) {
    const customHierarchyDimensions = columnsToDimensions(customHierarchyColumns);
    if (isValidArrayAndNotEmpty(customHierarchyDimensions)) {
      updatedAppliedDimensions = [...customHierarchyDimensions];
    }
  }
  if (!columnExists(Id, columnType, updatedAppliedDimensions, appliedMeasures, drilldownFields)) { return [sortState, false]; }
  const newSortState = sortState ? [...sortState] : [];
  const sortIndex = newSortState.findIndex((s) => s.Id === Id);
  const newOrder = disableInactiveSorting(newSortState, updatedAppliedDimensions, columnType, order, Id);

  if (!(newOrder || sortIndex >= 0)) { // removing an un-applied sort is not possible
    return [newSortState, false];
  }
  const deleteStart = deleteNonMandatory(newSortState, updatedAppliedDimensions, multiSort, isDrilldownPage || isReportListType); // delete non-mandatory sorts if not multi sorting
  if (newOrder && sortIndex >= 0 && sortIndex < deleteStart && sortIndex < newSortState?.length) { // replaces applied or non-deleted sort objects
    newSortState[sortIndex].Field = columnName;
    newSortState[sortIndex].Direction = newOrder;
    newSortState[sortIndex].Id = Id;
  } else if (!newOrder) { // removes the sort object
    newSortState.splice(sortIndex, 1);
  } else if (sortIndex < 0 || sortIndex >= deleteStart) { // pushes newly applied or just-deleted sort objects to the list
    newSortState.push({
      Field: columnName,
      Direction: newOrder,
      Id,
    });
  }
  return [rebuildSortState(newSortState, updatedAppliedDimensions, appliedMeasures, drilldownFields || [], !multiSort, isDrilldownPage, isReportListType), true];
};

export const rebuildSortState = (sortState: ISorting[], appliedDimensions: IDimension[], appliedMeasures: IColumn[], drilldownFields:IColumn[], noMultiSort?: boolean,
  isDrilldownPage : boolean = false, isReportListType?: boolean): ISorting[] => {
  const sortLookup = makeColumnSortMap(sortState); // sortLookup is a map of sort Field -> sort object with rank
  const makeDefault = (name: string, columnId: string) => makeDefaultSort(name, sortLookup, columnId); // creates a new sort object with rank -1, only if it does not exist

  const appliedColDimensions = appliedDimensions?.filter((dim) => isDimensionApplied(dim) && isDimensionColumn(dim));
  const appliedRowDimensions = appliedDimensions?.filter((dim) => isDimensionApplied(dim) && isDimensionRow(dim));
  const appliedDrilldownFields = isValidArrayAndNotEmpty(drilldownFields) ? drilldownFields.filter((col) => col.Props.Dimension.Applied === DimensionStatus.Applied) : [];
  const hasPivot = appliedColDimensions?.length > 0;

  const colDimSorts = isDrilldownPage ? [] : appliedColDimensions
    ?.map((dim): ISortingRanked => makeDefault(referredName(dim, appliedDimensions), referredDimId(dim)));
  let rowDimSorts: ISortingRanked[] = [];
  if (!isDrilldownPage && isValidArrayAndNotEmpty(appliedRowDimensions)) {
    if (isReportListType) {
      rowDimSorts = sortBy(appliedRowDimensions.filter((dim) => !!sortLookup[dim.Id] || !!sortLookup[getMetaFieldId(dim.Id)]) // filter basis Id for main column, basis ReferTo for meta column
        .map((dim): ISortingRanked => makeDefault(referredName(dim, appliedDimensions), referredDimId(dim))),
      (sort: ISortingRanked) => sort.rank);
    } else {
      rowDimSorts = appliedRowDimensions.slice(0, -1) // last row grouping is not a mandatory sort, if any
        .map((dim): ISortingRanked => makeDefault(referredName(dim, appliedDimensions), referredDimId(dim)));
    }
  }
  const measureSorts = hasPivot || isDrilldownPage ? [] : appliedMeasures // measures cannot be sorted in pivot mode or in drilldown mode as column is not present
    // use measures only if they were previously applied, since measures are not mandatory
    ?.filter((measure) => isMeasureAndApplied(measure) && !!sortLookup[measure.Id])
    ?.map((measure): ISortingRanked => makeDefault(measure.Name, measure.Id));
  const drilldownSorts = isValidArrayAndNotEmpty(appliedDrilldownFields) ? appliedDrilldownFields.filter((column) => !!sortLookup[column.Id]).map((col) => makeDefault(col.Name, referredDimId(col))) : [];
  const lastRowDimSort = isDrilldownPage || isReportListType ? [] : appliedRowDimensions // removing group sorts for drilldown page as well
    ?.slice(-1)
    // use last row grouping only if it was previously applied (since it is not mandatory)
    // or if sort list is going to be empty (apart from column groupings, since at least one other sort must be present)
    ?.filter((dim) => !!sortLookup[referredDimId(dim)] || (rowDimSorts.length === 0 && measureSorts.length === 0))
    ?.map((dim): ISortingRanked => makeDefault(referredName(dim, appliedRowDimensions), referredDimId(dim)));

  const restSorts = sortBy( // sort last row grouping and measures together by their previous ranks
    [...lastRowDimSort, ...drilldownSorts, ...measureSorts],
    (sort: ISortingRanked) => (sort.rank === -1 ? sortState.length : sort.rank), // newly added sorts are pushed to the end
  )
    ?.slice(0, noMultiSort ? 1 : undefined); // take only the first item if not multi sorting, else take the whole array

  return [...colDimSorts, ...rowDimSorts, ...restSorts]
    ?.map((sort): ISorting => ({
      ...sort,
      rank: undefined,
    } as ISortingRanked)); // remove rank field to avoid confusion
};

const columnExists = (Id: string, columnType: draggableItemType, dimensions: IDimension[], measures: IColumn[], drilldownFields: IColumn[]): boolean => {
  let columns: (IDimension | IColumn)[] = [];
  if (columnType === draggableItemType.Measure) {
    columns = measures ?? [];
  } else if (columnType === draggableItemType.RowDim || columnType === draggableItemType.ColumnDim) {
    columns = dimensions ?? [];
  } else if (columnType === draggableItemType.DrilldownDim) {
    columns = drilldownFields;
  }
  if (isValidArrayAndNotEmpty(columns)) {
    return columns?.findIndex((col) => referredDimId(col) === Id) !== -1;
  }
  return false;
};

const disableInactiveSorting = (
  sortState: ISorting[], appliedDimensions: IDimension[],
  columnType: draggableItemType,
  order: SortingOrderTypes | null,
  Id: string,
): SortingOrderTypes | null => {
  if (!(order !== SortingOrderTypes.ASC && order !== SortingOrderTypes.DESC) || columnType === draggableItemType.DrilldownDim) return order;

  let disable = columnType === draggableItemType.ColumnDim; // column grouping sort cannot be disabled
  if (!disable) { // last sort of the list (apart from column grouping) should not be removed to avoid empty sort list
    const columnGroupings = appliedDimensions.filter((dim) => isDimensionApplied(dim) && isDimensionColumn(dim)).length;
    disable = sortState.length <= columnGroupings + 1 && sortState[sortState.length - 1]?.Id === Id;
  }
  if (disable) {
    return SortingOrderTypes.ASC;
  }
  return order;
};

const deleteNonMandatory = (sortState: ISorting[], appliedDimensions: IDimension[], multiSort: boolean, isDrilldownPage:boolean): number => {
  let deleteStart = sortState.length; // delete nothing if multi sort is enabled
  if (!multiSort) {
    deleteStart = appliedDimensions
      .filter((dim) => isDimensionApplied(dim) && (isDimensionRow(dim) || isDimensionColumn(dim)))
      .length - 1; // column groupings and row groupings minus 1 are mandatory
    deleteStart = deleteStart < 0 ? 0 : deleteStart;
    if (isDrilldownPage) { // if not multisort and is a drilldown page then remove the existing items from store state
      sortState.splice(0);
      return 0;
    }
    sortState.splice(deleteStart);
  }
  return deleteStart;
};

const makeDefaultSort = (name: string, sortLookup: columnSortMap, columnId: string): ISortingRanked => ({ // creates a new sort object with rank -1, only if it does not exist
  Field: name,
  Direction: SortingOrderTypes.ASC,
  Id: columnId,
  rank: -1, // used to push the newly added sort object to the end of the list
  ...sortLookup[columnId], // override above values with existing sort object, if any
});

// Valid funnel "Rows" option ids — mirrors EditFunnel's dimensionOptions exactly: keep only
// applied dimensions, then the row-group dimensions that are not a metafield referenced by another
// dimension and not table-hidden (a metafield-backed dimension with Props.ReferTo is kept even when
// hidden), each mapped through its metafield ReferTo id (a metafield-backed dimension plots via
// Props.ReferTo, so funnelDimensionId may equal that id).
const getValidFunnelDimensionIds = (appliedDimensions: IDimension[]): string[] => {
  const applied = (appliedDimensions || []).filter((dimension) => dimension.Applied);
  const referredIds = new Set(
    applied.map((dimension) => dimension.Props?.ReferTo).filter(Boolean),
  );
  return applied
    .filter((dimension) => {
      if ((dimension?.Props?.Mode === ColumnModes.Hidden && !dimension?.Props?.ReferTo)
        || referredIds.has(dimension.Id)
        || dimension?.DimensionMode !== DimensionMode.RowGroup
      ) {
        return false;
      }
      return true;
    })
    .map((dimension) => dimension.Props?.ReferTo || dimension.Id);
};

// Valid funnel "Values" option ids — mirrors EditFunnel's measureOptions: applied, visible numeric
// measures (a funnel stage plots a numeric magnitude) whose expression is not broken. The numeric
// check mirrors chart.utility.isNumericMeasure, inlined here to avoid a report.util ↔ chart.utility
// import cycle; the broken-expression check mirrors EditFunnel's brokenExpressionIds guard so a
// measure whose expression references a deleted/unchecked custom-filter variable is never a valid
// funnel value option.
const getValidFunnelMeasureIds = (appliedMeasures: IColumn[], brokenMeasureIds: Set<string>): string[] => (appliedMeasures || [])
  .filter((measure) => measure?.Props?.Mode !== ColumnModes.Hidden
      && measure?.Props?.Mode !== ColumnModes.NotApplied
      && isNumericMeasure(measure)
      && !brokenMeasureIds.has(String(measure?.BuilderConfig?.uniqueKey ?? measure?.Id)))
  .map((measure) => measure.Id);

// Reconcile a funnel chart's saved dimension / measure selection against the report's currently
// applied columns. If a saved id is no longer a valid option (its column was removed or hidden),
// fall back to the first valid option — or '' when none remain — so the persisted config and the
// live preview always reference a real column (matching FunnelChartUtil's own first-option fallback).
export const reconcileFunnelAttributes = (
  attributes: IChartAttributes,
  appliedDimensions: IDimension[],
  appliedMeasures: IColumn[],
  expressionsList: IExpressionStoreLike,
  customVariablesList: ICustomFilterDefinition[],
): IChartAttributes => {
  // Broken measure expressions — those referencing a custom-filter variable that is no longer valid
  // (definition deleted or UseVar unchecked). Valid variable IDs are the UseVar=true definitions, so a
  // measure keyed by its expression uniqueId here must be excluded from the funnel value options.
  // Mirrors report-builder-store.selectors' brokenCustomFilterExpressionsSelector.
  const validVariableIds = new Set((customVariablesList || []).filter((definition) => definition.UseVar).map((definition) => definition.ID));
  const brokenMeasureIds = new Set(
    getBrokenCustomFilterExpressions(expressionsList, validVariableIds).map((broken) => String(broken.uniqueId)),
  );
  const validDimensionIds = getValidFunnelDimensionIds(appliedDimensions);
  const validMeasureIds = getValidFunnelMeasureIds(appliedMeasures, brokenMeasureIds);
  const funnelDimensionId = validDimensionIds.includes(attributes?.funnelDimensionId)
    ? attributes.funnelDimensionId
    : (validDimensionIds[0] || '');
  const funnelMeasureId = validMeasureIds.includes(attributes?.funnelMeasureId)
    ? attributes.funnelMeasureId
    : (validMeasureIds[0] || '');
  return { ...attributes, funnelDimensionId, funnelMeasureId };
};

// drop broken custom variable expression
export const excludeBrokenComboMeasures = (
  appliedMeasures: IColumn[],
  expressionsList: IExpressionStoreLike,
  customVariablesList: ReportResponseModel.ICustomFilterDefinition[],
): IColumn[] => {
  const validVariableIds = new Set((customVariablesList || []).filter((definition) => definition.UseVar).map((definition) => definition.ID));
  const brokenMeasureIds = new Set(getBrokenCustomFilterExpressions(expressionsList, validVariableIds).map((broken) => String(broken.uniqueId)));
  return (appliedMeasures || []).filter((measure) => !brokenMeasureIds.has(String(measure?.BuilderConfig?.uniqueKey ?? measure?.Id)));
};

export const getUpdatedReportConfig = (reportStore: IReportState, isSaveAs:boolean = false):IReportConfig => {
  const addedFilter = reportStore.filterConfig.filter(
    (config: IFilterConfig) => config.IsRemoved !== true && !config?.ShowInMainReport,
  );

  const addedBuilderFilter = reportStore.filterConfig.filter(
    (config: IFilterConfig) => config.IsRemoved !== true && !config?.ShowInMainReport,
  );
  const addedSubBuilderFilters = reportStore.filterConfig.filter(
    (config: IFilterConfig) => config.IsRemoved !== true && config.QueryId,
  );
  let builderMeasures: IBuilderMeasure[] = [];
  const mainReportFilterMetaInfo = getFilterMetaDataInfo(addedFilter, reportStore.appliedFilters) || reportStore.reportConfig?.FiltersMetadata;
  const reportFilterMetaInfo = {
    ...mainReportFilterMetaInfo,
    SubBuilderFiltersMetadata: reportStore.reportConfig?.FiltersMetadata?.SubBuilderFiltersMetadata || [],
  };
  const nameSpaceInfo = reportStore.savedReportConfig?.ConfigMetadata?.namespaceTypes;
  const updatedReportConfig:IReportConfig = {
    ...reportStore.savedReportConfig,
    Sorting: [...reportStore.sort],
    Paging: { ...reportStore.page },
    FiltersMetadata: reportFilterMetaInfo,
    ConfigMetadata: undefined,
  };
  if (isSaveAs) {
    updatedReportConfig.ReportConfigId = '';
    updatedReportConfig.Details = {
      Title: reportStore.reportNameSaveAs,
      Description: reportStore.reportDescriptionSaveAs,
      Type: ReportType.Custom,
    };
    updatedReportConfig.SourceInfo = {
      Id: reportStore.reportConfig.ReportConfigId,
      Type: ReportSourceInfo.Report,
    };
  }

  // Funnel charts: drop a stale funnelDimensionId / funnelMeasureId that points to a dimension or
  // measure removed (or hidden) from the report before it is persisted — same reconciliation the
  // report-builder save/preview path applies, using the report page's currently applied columns.
  // On the report page a measure's expression lives inline on Props.Measure.Expression (there is no
  // separate expressionStore), so adapt the applied measures into the expression-store-like shape the
  // broken-expression detection expects — keyed by measure Id (matching getValidFunnelMeasureIds'
  // BuilderConfig.uniqueKey ?? Id fallback). Mirrors report-store's reportCustomExpressionStoreSelector.
  const reportExpressionStore: IExpressionStoreLike = {};
  (reportStore.allMeasures || []).forEach((measure) => {
    const measureExpression = measure?.Props?.Measure?.IsCustomExpression && measure?.Props?.Measure?.Expression;
    if (measureExpression) {
      reportExpressionStore[String(measure.Id)] = { displayName: measure.Name, jsonExpression: measureExpression };
    }
  });
  const visualizationAttributes = reportStore?.Visualization?.type === VisualizationTypes.FunnelChart
    ? reconcileFunnelAttributes(
      reportStore?.Visualization?.attributes,
      reportStore.appliedDimensions,
      reportStore.allMeasures,
      reportExpressionStore,
      reportStore.reportConfig?.CustomFilterDefinitions,
    )
    : reportStore?.Visualization?.attributes;

  updatedReportConfig.Visualization = getUpdateVisualization(updatedReportConfig.Details.Title, reportStore?.Visualization?.type,
    visualizationAttributes, reportStore?.reportConfig.Visualization?.DrillDownConfigMapping, reportStore?.reportConfig.Visualization?.DrilldownConfigIdMapping, reportStore?.reportConfig?.Visualization?.DrillDownPagination, null, reportStore?.ConditionalFormatting, reportStore?.reportConfig?.Visualization?.LinkedReportsConfig, reportStore?.Visualization?.Builder);

  const isAugumented = reportStore.savedReportConfig?.Builder?.length > 1;
  updatedReportConfig.Builder = reportStore.savedReportConfig.Builder.map(
    (builderItem: IBuilder) => {
      builderMeasures = [...builderMeasures, ...builderItem.Select.Measures];
      if (updatedReportConfig?.CalculatedColumns) {
        builderMeasures = builderMeasures.concat(updatedReportConfig.CalculatedColumns).flat();
      }
      return ({
        ...builderItem,
        Select: {
          ...builderItem.Select,
          Dimensions: getSavePayloadBuilderDimensions(reportStore?.activeDimensions, [...builderItem?.Select?.Dimensions], reportStore?.filterConfig, nameSpaceInfo, builderItem.Namespace, isAugumented),
          Measures: getMeasuresByBuilderType(reportStore, builderItem),
        },
        Filter: getSavePayloadBuilderFilters(addedBuilderFilter, reportStore.appliedFilters, builderItem.Filter),
      });
    },
  );
  const updatedDimensions = [...reportStore.activeDimensions];
  reportStore.columns.forEach((item) => {
    if (!item?.Props?.Dimension && item?.Props?.ParentDimension) {
      updatedDimensions.push(convertColumnToDimension(item));
    }
  });
  // If filter is popped out from query,
  // recalculate that filter's default value on Save/SaveAs
  // and keep the rest of the filters intact
  if (isValidArrayAndNotEmpty(updatedReportConfig.SubBuilders)) {
    updatedReportConfig.SubBuilders.forEach((builder) => {
      const filterGroup = builder.Filter?.FilterGroup;
      filterGroup?.forEach((filter) => {
        const matchedSubBuilderFilter = addedSubBuilderFilters.find(
          (addedSubBuilderFilter) => addedSubBuilderFilter?.ID === filter?.ID,
        );
        // Custom-filter constituents in SubBuilders are not present in reportStore.filterConfig on
        // the report view (the render config excludes SubBuilder filters), so matchedSubBuilderFilter
        // misses them. Allow the DefaultValue refresh whenever the parent wrapper has an applied
        // value — otherwise the updated custom filter value never propagates to constituents during
        // Save / Save As.
        const isCustomConstituent = !!filter.AssociatedCustomFilterId
          && !!reportStore.appliedFilters?.[filter.AssociatedCustomFilterId];
        if (matchedSubBuilderFilter || isCustomConstituent) {
          const appliedKey = isCustomConstituent ? filter.AssociatedCustomFilterId : filter?.ID;
          if (isLargeOptionSetFilter(filter)) {
            filter.Render.DefaultValue = reportStore.appliedFilters[appliedKey]?.FilterResponse;
          } else {
            const correspondingAppliedFilter = reportStore.appliedFilters[appliedKey];
            // The Operator is generally present for number filters, so we need to set it here
            if (correspondingAppliedFilter?.FilterResponse?.Operator) {
              filter.Operator = correspondingAppliedFilter?.FilterResponse?.Operator;
            }
            filter.Render.DefaultValue = getDefaultValue(filter, reportStore.appliedFilters);
            // For UserMultiSelect, the select-type (IN / NOTIN / ALL) lives in
            // DropdownMetaData.DropdownSelectType, not in DefaultValue (which only holds the
            // UserIds). The line above refreshes the IDs from the current applied value but
            // leaves DropdownMetaData carrying the originally-loaded select-type — so a value
            // saved as IN-3 next to a stale NOTIN reads back as "everyone except those 3" on
            // reload. Restamp the select-type from the same applied value, mirroring the
            // main-builder path in getSavePayloadBuilderFilters.
            if (filter.Render.Type === FilterType.UserMultiSelect && correspondingAppliedFilter?.FilterResponse?.Type) {
              filter.Render.DropdownMetaData = {
                ...filter.Render.DropdownMetaData,
                DropdownSelectType: correspondingAppliedFilter.FilterResponse.Type,
              };
            }
          }
        }
      });
    });
  }

  updatedReportConfig.Columns = [...getSavePayloadDimensionColumns(updatedDimensions, reportStore?.filterConfig), ...getSavePayloadMeasureColumns(
    reportStore?.allMeasures,
    getDefaultMeasures(builderMeasures, reportStore.columns),
    reportStore.savedReportConfig,
  )];
  // append entity group meta dimensions to columns
  updatedReportConfig.Columns = processEntityGroupDimensions(updatedReportConfig.Columns, updatedDimensions, reportStore.entityGroupStore, reportStore.entityGroupOptionsMap);
  updatedReportConfig.Sorting = getUpdatedSortObjectForSave(updatedDimensions, reportStore.sort);

  // update the default value in the custom filter definitions as per the applied filters
  if (isValidArrayAndNotEmpty(updatedReportConfig.CustomFilterDefinitions)) {
    updatedReportConfig.CustomFilterDefinitions = updatedReportConfig.CustomFilterDefinitions.map(
      (definition) => {
        const appliedValue = reportStore.appliedFilters?.[definition.ID]?.FilterResponse;
        // Only refresh when the report page carries a value for this custom filter; otherwise
        // keep the saved DefaultValue intact (a definition with no applied entry is untouched).
        if (appliedValue === undefined) {
          return definition;
        }
        return { ...definition, DefaultValue: appliedValue };
      },
    );
  }

  globalLogger(`report store ${reportStore}`);
  globalLogger(`updateReportConfig ${updatedReportConfig}`);

  if (Object.prototype.hasOwnProperty.call(updatedReportConfig, 'FeatureContext') && !updatedReportConfig.FeatureContext) {
    updatedReportConfig.FeatureContext = FeatureContext.Sales;
  }
  return updatedReportConfig;
};

export const getDefaultMeasures = (builderMeasures: IBuilderMeasure[], configColumns: IReportBuilderColumn[]) => {
  const defaultMeasures : IReportBuilderColumn[] = [];

  configColumns.forEach((item) => {
    const builderMeasure = builderMeasures?.find((measure) => measure.Id === item.Id);
    if (builderMeasure) {
      // eslint-disable-next-line no-param-reassign
      item.Props.IsAdditional = builderMeasure.IsAdditional;
      defaultMeasures.push(item);
    }
  });

  return defaultMeasures;
};

const getMeasuresByBuilderType = (reportStore: IReportState, builder:IBuilder):Array<IBuilderMeasure> => {
  const appliedMeasures = reportStore.allMeasures.filter((item: IColumn) => isMeasureAndApplied(item));
  const buidlerMeasure = getSavePayloadBuilderMeasures(appliedMeasures, builder.Select.Measures);
  return buidlerMeasure;
};

export const getUpdateVisualization = (
  visualizatonDisplayName:string,
  type: VisualizationTypes,
  attributes: IChartAttributes,
  drilldownConfigMapping?: DrilldownConfigMapping,
  drilldownConfigIdMapping?: DrilldownConfigMapping,
  drilldownPagination?:IPaging,
  reportType?: ReportBuilderType,
  conditionalFormatting?: IConditionalFormatting,
  linkedReportsConfig?: ILinkedConfig[],
  comboBuilder?: ReportResponseModel.IChartVisualizationBuilder,
):IPanelVisualisation => {
  if (isListReport(reportType)) {
    const updatedVisualisationForListReport :IPanelVisualisation = {
      DisplayName: visualizatonDisplayName,
      Type: VisualizationTypes.Table,
      Builder: {
        showDataLabels: false,
        stacked: false,
        fullStacked: false,
        showPercentage: false,
        showSharedTooltip: false,
        hideLegend: false,
        showCategoryName: false,
        showSeriesLabel: false,
        showSubTotals: false,
        showSummary: false,
        showWrappedHeader: attributes?.showWrappedHeader,
      },
      ConditionalFormatting: conditionalFormatting ? { ...conditionalFormatting, Enabled: true } : conditionalFormatting,
      LinkedReportsConfig: linkedReportsConfig,
    };
    return updatedVisualisationForListReport;
  }
  const updatedVisualisation :IPanelVisualisation = {
    DisplayName: visualizatonDisplayName,
    Type: type,
    Builder: {
      Properties: {
        ...attributes,
      },
      ...(type === VisualizationTypes.ComboChart && comboBuilder?.ComboConfig ? {
        XAxis: comboBuilder.XAxis,
        YAxis: comboBuilder.YAxis,
        ComboConfig: comboBuilder.ComboConfig,
      } : {}),
    },
    DrilldownConfigMapping: drilldownConfigMapping,
    DrilldownConfigIdMapping: drilldownConfigIdMapping,
    DrilldownPagination: drilldownPagination,
    ConditionalFormatting: conditionalFormatting ? { ...conditionalFormatting, Enabled: true } : conditionalFormatting,
    LinkedReportsConfig: linkedReportsConfig,
  };

  return updatedVisualisation;
};

export const removeQueryParamsDataInURL = (selectingFromSideBarOrRefresh?: boolean) => {
  const queryParams = new URLSearchParams(window.location.search);

  if (selectingFromSideBarOrRefresh) {
    queryParams.delete(QueryParamNames.SourceId);
    if (queryParams.get(QueryParamNames.DrilldownStateID)) {
      queryParams.delete(QueryParamNames.DrilldownStateID);
    }
  }

  return `?${queryParams.toString()}`;
};

export const getIsValidInput = (
  inputString: string, maxLength: number, minLength:number,
) => isWrongInput(inputString)
  || inputString.length > maxLength
  || inputString.trim().length < minLength;

export const getIsReportBuilderSaveFormDisable = (
  Title: string,
  Description: string,
) => getIsValidInput(Title, MAX_NAME_CHARACTER_LIMIT, MIN_CHARACTER_LIMIT) || getIsValidInput(Description, MAX_DESC_CHARACTER_LIMIT, MIN_CHARACTER_LIMIT);

export const getErrMsg = (
  inputString: string, type:string, Maxlimit:number,
) => {
  if (isWrongInput(inputString)) return `${useLocalize('err.inValidCharacterMessage')}`;

  if (inputString.length > Maxlimit) return `${useLocalize(`err.${type}MaxLength`)}`;

  if (inputString.trim().length < MIN_CHARACTER_LIMIT) return `${useLocalize(`err.${type}MinLength`)}`;

  return '';
};

export const convertColumnToDimension = (column: IColumn) : IDimension => {
  const props = column?.Props;

  return ({
    Name: column?.Name,
    IsMasked: props?.IsMasked,
    BuilderConfig: column?.BuilderConfig,
    Mode: props?.Mode,
    IsUserDynamicField: props.IsUserDynamicField,
    Props: props,
    ReferTo: props?.ReferTo,
    Applied: props?.Dimension?.Applied,
    Orderable: props?.Dimension?.Orderable,
    ZeroAnchor: props?.Dimension?.ZeroAnchor,
    DimensionMode: props?.Dimension?.DimensionMode,
    Id: column.Id,
  });
};

export const getUpdatedSortObjectForSave = (dimensions: IDimension[], sort:ISorting[]) => {
  const sorting: ISorting[] = [];

  sort.forEach((item:ISorting) => {
    const dimension = dimensions.find((dim) => dim?.Id === item.Id);
    if (dimension) {
      sorting.push({
        Field: dimension?.BuilderConfig?.IsDynamicField ? (dimension.BuilderConfig.DisplayName || dimension?.Name) : dimension?.Name,
        Direction: item.Direction,
        Id: dimension.Id,
      });
    } else {
      sorting.push({
        Field: item.Field,
        Direction: item.Direction,
        Id: item.Id,
      });
    }
  });

  return sorting;
};

export const getInitialKey = (type: VisualizationTypes) => {
  switch (type) {
    case VisualizationTypes.PieChart:
    case VisualizationTypes.DonutChart:
      return ChartAttributesKey.Pie;
    case VisualizationTypes.FunnelChart:
      return ChartAttributesKey.Funnel;
    case VisualizationTypes.LineChart:
    case VisualizationTypes.AreaChart:
      return ChartAttributesKey.Line;
    case VisualizationTypes.Table:
      return ChartAttributesKey.Table;
    case VisualizationTypes.ComboChart:
      return ChartAttributesKey.Combo;
    default:
      return ChartAttributesKey.Column;
  }
};

// Create a list of entities names which are present in the dimensions and measures.
// @fieldList -> list of dimensions or measure from which entity needs to be fetch.
// return an sorted array of string as entity name.
export const getEntityList = <T extends IDimension | IColumn>(fieldsList: T[]) : EntityList[] => {
  const entityList: EntityList[] = [];
  if (isValidArrayAndNotEmpty(fieldsList)) {
    fieldsList.forEach((item) => {
      if (item?.BuilderConfig?.EntityName) {
        entityList.push({ entity: item?.BuilderConfig?.EntityName, isQuery: true });
      } else if (item?.BuilderConfig?.Entity) {
        entityList.push({ entity: item?.Props?.Namespace || item?.BuilderConfig?.Entity, isQuery: false });
      }
    });
  }
  return sortBy(uniqBy(entityList, 'entity'), 'entity');
};

// Update the Columns object with the active dimensions and returns an updated list of columns
// @Columns: Array of columns.
// @ActiveDimensions: Array of all applied dimensions
// @allMeasures: Array of all measures.
// @dynamicDimensions: Array of all dynamicDimensions.
export const getColumns = (
  columns: ReportResponseModel.IColumn[], activeDimensions: ReportReduxModel.IDimension[],
  allMeasures: ReportResponseModel.IColumn[], dynamicDimensions:ReportResponseModel.IColumn[],
  entityGroupStore: Record<string, string>, entityGroupOptionsMap: Record<string, IDimensionMetaField[]>,
):IColumn[] => {
  if (!isEmpty(columns) && !isEmpty(activeDimensions)) {
    return getColumnsForActiveDimensions(columns, activeDimensions, allMeasures, dynamicDimensions, entityGroupStore, entityGroupOptionsMap).map(removeBuilderPropForMeasure);
  }

  return isValidArrayAndNotEmpty(columns) ? columns.map(removeBuilderPropForMeasure) : [];
};

// Payload Creation for report and export done in report page, Returns the complete object as ReportFetchCriteria
// @Input: Take all the redux stores value and create the required payload.
export const getReportDataLoadPayload = (filterConfig: Array<ReportResponseModel.IFilterConfig>,
  filters: ObjModel.ObjGeneric<FilterModel.IFilterResponse>, reportId: string,
  sort: Array<ReportResponseModel.ISortedField>, page: ReportResponseModel.IPage,
  columns: Array<ReportResponseModel.IColumn>, activeDimensions: Array<ReportReduxModel.IDimension>,
  allMeasures: Array<ReportResponseModel.IColumn>, dynamicDimensions:Array<ReportResponseModel.IColumn>,
  entityGroupStore: Record<string, string>, entityGroupOptionsMap: Record<string, IDimensionMetaField[]>,
  isReportListType: boolean, filterMetaData?: IFiltersMetaData, customHierarchies?: ICustomHierarchy[],
  customHierarchyDrilldownData?: ReportReduxModel.ICustomHierarchyDrilldownData,
  travelBackToMainReportFromCustomHierarchyReport?: boolean,
  drilldownStatePayload?: ObjModel.Obj | null,
  subBuilders?: IBuilder[],
  customFilterDefinitions?: ReportResponseModel.ICustomFilterDefinition[]): ReportFetchCriteria => {
  let updatedActiveDimensions = activeDimensions;
  if (isValidArrayAndNotEmpty(customHierarchyDrilldownData?.columns)) {
    const customHierarchyDimensions = columnsToDimensions(customHierarchyDrilldownData?.columns);
    if (isValidArrayAndNotEmpty(customHierarchyDimensions)) {
      updatedActiveDimensions = [...customHierarchyDimensions];
    }
  }
  const data = {
    ReportId: reportId,
    Filters: {},
    FiltersMetadata: {},
    Page: page,
    Columns: getColumns(columns, activeDimensions, allMeasures, dynamicDimensions, entityGroupStore, entityGroupOptionsMap),
    SelectedMeasure: '',
    SelectedRow: [],
    Sort: rebuildSortState(sort, updatedActiveDimensions, getAppliedMeasures(columns), [], false, false, isReportListType),
    AppliedHierarchy: [],
  } as ReportFetchCriteria;

  return getUpdatedReportData(filterConfig, filters, data, filterMetaData, customHierarchies, customHierarchyDrilldownData, travelBackToMainReportFromCustomHierarchyReport, drilldownStatePayload, subBuilders, customFilterDefinitions);
};

// Get Updated data Object, updates the filters and filterMetaData, Returns the updated data Object.
// @filterConfig: Filter Config stored in store.
// @filters: Array of all applied filters.
// @data: ReportFetch Payload data Object.
// @drilldownStatePayload: temp drilldown state captured for linked-insight target loads;
//   used to overlay parent-mapped values onto custom-filter constituents at fetch-payload
//   build time, sidestepping the resolveSubBuilderCustomFilterDefaults overwrite race.
export const getUpdatedReportData = (filterConfig: Array<ReportResponseModel.IFilterConfig>, filters: ObjModel.ObjGeneric<FilterModel.IFilterResponse>, data: ReportFetchCriteria, filterMetaData?: IFiltersMetaData, customHierarchies?: ICustomHierarchy[], customHierarchyDrilldownData?: ReportReduxModel.ICustomHierarchyDrilldownData, travelBackToMainReportFromCustomHierarchyReport?: boolean, drilldownStatePayload?: ObjModel.Obj | null, subBuilders?: IBuilder[], customFilterDefinitions?: ReportResponseModel.ICustomFilterDefinition[]) => {
  const updatedData = cloneDeep(data);
  let isUserFilterMetadataComputed = false;
  updatedData.Filters = isValidArrayAndNotEmpty(filterConfig) ? filterConfig.reduce((acc: ObjModel.Obj, item) => {
    // If filterConfig is a custom hierarchy filter, add it to the AppliedHierarchy array
    if (item.CustomHierarchyId) {
      return acc;
    }
    // Skip constituent filters — identified by AssociatedCustomFilterId being set AND
    // IsCustomFilter being falsy (i.e. this is an underlying filter belonging to a
    // custom filter, not the virtual custom-filter entry itself). Their payload values
    // are written by the custom-filter fan-out branch below, which reads
    // filters[<customFilterId>] (the single source of truth for all constituents).
    // Without this skip, the regular-filter branch would re-write the same key with
    // filters[<constituentId>] — the constituent's own, potentially stale, applied
    // value — and override the fan-out, losing linked-insight drilldown values
    // hydrated into the custom filter at report-config load time.
    if (item.AssociatedCustomFilterId && !item.IsCustomFilter) {
      return acc;
    }
    // Expand custom filters into their constituent filters for the API payload.
    // See applyValueToConstituent in common.utility.ts for the envelope rules.
    if (item.IsCustomFilter && isValidArrayAndNotEmpty(item.LinkedFilters) && filters[item.ID] && !filters[item.ID].Disabled) {
      const customFilterResponse = filters[item.ID].FilterResponse;
      const isCustomFilterLOS = isLargeOptionSetFilter(item);
      item.LinkedFilters.forEach((linkedFilter) => {
        const result = applyValueToConstituent(
          linkedFilter,
          customFilterResponse,
          item,
          isCustomFilterLOS,
          filterConfig,
          subBuilders,
        );
        acc[result.key] = result.value;
        if (result.userDropdownSelectType !== undefined) {
          updatedData.FiltersMetadata.UserDropdownSelectType = result.userDropdownSelectType;
        }
        if (result.subBuilderMetadata) {
          if (!updatedData.FiltersMetadata.SubBuilderFiltersMetadata) {
            updatedData.FiltersMetadata.SubBuilderFiltersMetadata = [];
          }
          updatedData.FiltersMetadata.SubBuilderFiltersMetadata.push(result.subBuilderMetadata);
        }
      });
      // Linked-insight override: when this report was loaded as a drilldown target
      // and LinkedReportsConfig maps a parent filter onto this custom filter, the
      // parent-mapped value must win over the fan-out default that just ran. Walk
      // the parent's LinkedReportsConfig for entries pointing at this report id and,
      // for any { sourceFilterId → targetFilterId } where targetFilterId === item.ID,
      // recompute each constituent's payload key (with the same idempotent _QueryId
      // suffix used above) and overwrite acc[linkedId] with the override value.
      const drilldownFilters = drilldownStatePayload?.Filters as ObjModel.Obj | undefined;
      const drilldownLinkedReportsConfig = drilldownStatePayload?.LinkedReportsConfig as ILinkedConfig[] | undefined;
      if (drilldownFilters && isValidArrayAndNotEmpty(drilldownLinkedReportsConfig)) {
        let overrideValue: unknown;
        let hasOverride = false;
        drilldownLinkedReportsConfig.forEach((entry) => {
          if (entry?.LinkedReportId !== data.ReportId) return;
          const filterMappings = entry?.LinkingConfig?.Filters;
          if (!filterMappings) return;
          Object.entries(filterMappings).forEach(([sourceFilterId, targetFilterId]) => {
            if (targetFilterId !== item.ID) return;
            if (drilldownFilters[sourceFilterId] === undefined) return;
            overrideValue = drilldownFilters[sourceFilterId];
            hasOverride = true;
          });
        });
        if (hasOverride) {
          item.LinkedFilters.forEach((linkedFilter) => {
            const isSubBuilderConstituent = linkedFilter.Source === LinkedFilterSource.SubBuilder
              && linkedFilter.QueryId;
            let linkedId = linkedFilter.FilterId;
            if (isSubBuilderConstituent) {
              const suffix = `_${linkedFilter.QueryId}`;
              linkedId = linkedFilter.FilterId.endsWith(suffix)
                ? linkedFilter.FilterId
                : `${linkedFilter.FilterId}${suffix}`;
            }
            acc[linkedId] = overrideValue;
          });
        }
      }
      return acc;
    }
    // Remove Disabled filters
    if (filters[item.ID] && !filters[item.ID].Disabled) {
      if (item.Type === FilterType.UserMultiSelect && item?.IsPrimaryUserFilter) {
        acc[item.ID] = filters[item.ID].FilterResponse.UserIds;
        isUserFilterMetadataComputed = true;
        updatedData.FiltersMetadata.UserDropdownSelectType = filters[item.ID].FilterResponse.Type;
        updatedData.FiltersMetadata.Status = item.Metadata?.Status ?? userStatusEnum.Both;
      } else if (item.Type === FilterType.UserMultiSelect) {
        acc[item.ID] = {
          DropdownMetaData: {
            DropdownSelectType: filters[item.ID].FilterResponse?.Type,
            UserStatusType: item?.DropdownMetaData?.UserStatusType,
          },
          Value: filters[item.ID].FilterResponse.UserIds,
        };
      } else {
        acc[item.ID] = filters[item.ID].FilterResponse;
      }
    }
    return acc;
  }, {}) : null;

  // Populate the AppliedHierarchy array with filters that contain CustomHierarchyId
  const customHierarchyId = filterConfig.find((item) => !!item.CustomHierarchyId)?.CustomHierarchyId;
  if (customHierarchyId && isValidArray(updatedData?.AppliedHierarchy)) {
    const lastHierarchyLevel = customHierarchyDrilldownData?.hierarchyChain?.[customHierarchyDrilldownData.hierarchyChain.length - 1];
    updatedData.AppliedHierarchy.push({
      HierarchyID: customHierarchyId,
      LastAppliedColID: lastHierarchyLevel?.columnId || '',
      /**
       * If the user is at the main report level, clear out the applied hierarchy filters.
       */
      Filters: travelBackToMainReportFromCustomHierarchyReport ? {} : filterConfig.reduce((acc: ObjModel.Obj, item) => {
        // Filters applied with the custom hierarchy drilldown workflow have the CUSTOM_HIERARCHY_FILTER_SUFFIX added to them.
        // Below logic handles those scenarios.
        if (!filters[item.ID] && !filters[`${item.ID}${CUSTOM_HIERARCHY_FILTER_SUFFIX}`]) return acc;
        if (item?.CustomHierarchyId === customHierarchyId) {
          const updatedIdToCheck = `${item.ID}${CUSTOM_HIERARCHY_FILTER_SUFFIX}`;
          if ([
            FilterType.LSQMetadataMultiSelect,
            FilterType.LSQMetadataSingleSelect,
            FilterType.CustomDefinedMultiSelect,
            FilterType.CustomDefinedSingleSelect,
          ].includes(item?.Type) && (
            !filters[updatedIdToCheck]?.FilterResponse?.length
            || filters[updatedIdToCheck]?.FilterResponse === DimensionNoValue
            || filters[updatedIdToCheck]?.FilterResponse === DimensionNullValue
          )
          ) {
            acc[item.ID] = [''];
          } else if (filters[updatedIdToCheck]?.FilterResponse === DimensionNoValue || filters[updatedIdToCheck]?.FilterResponse === DimensionNullValue) {
            /**
             * Specific handling for text type of filters is added here where we send empty quotes as filter value so that
             * backend processes this empty value rather than ignoring it.
             */
            if (item?.Type === FilterType.MultiTextSearch) {
              acc[item.ID] = [''];
            } else {
              acc[item.ID] = [];
            }
          } else {
            acc[item.ID] = filters[updatedIdToCheck]?.FilterResponse;
          }
        }
        return acc;
      }, {}),
    });
  }
  // when user filters are popped out of the query to main builder, below condition should be removed
  // and appropriate logic should be written to handle SubBuilderFiltersMetadata
  if (isUserFilterMetadataComputed) {
    updatedData.FiltersMetadata.SubBuilderFiltersMetadata = filterMetaData?.SubBuilderFiltersMetadata;
  }
  updatedData.FiltersMetadata = (isValidObjectAndNotEmpty(filterMetaData) && !isUserFilterMetadataComputed) ? filterMetaData : updatedData.FiltersMetadata;

  // Build the V1 CustomFilterVariables map (custom-filter-expressions feature). For every
  // UseVar=true definition, send its current value keyed by the definition's **ID** — the same ID a
  // CustomFilterVariableExpr node stores as its Value — so the backend resolves the placeholders in
  // measure expressions. Falls back to the definition's DefaultValue when there is no live value, so
  // every UseVar definition is present (the backend returns HTTP 400 on a missing key). The key is
  // omitted entirely when the report has no UseVar definitions. V2 (getFetchDrilldownReportV2) sends
  // nothing — it never calls this.
  //
  // NOTE: the raw FilterResponse is sent as-is for now. The backend value-shape contract expects
  // per-DataType/Operator operand shapes (string → bare array/string, number BETWEEN with the
  // FE-only `Type:'Absolute'` stripped, etc.); a `shapeCustomFilterVariable` step (Task 7) may be
  // needed to avoid 400s on those cases — to be revisited. Likewise DataType is threaded through
  // verbatim (no StringArray→String / Int64Array→Int64 normalization yet).
  const useVarDefinitions = (customFilterDefinitions || []).filter((def) => def.UseVar);
  if (isValidArrayAndNotEmpty(useVarDefinitions)) {
    const customFilterVariables: { [id: string]: any } = {};
    useVarDefinitions.forEach((def) => {
      const liveValue = filters?.[def.ID]?.FilterResponse;
      customFilterVariables[def.ID] = liveValue ?? def.DefaultValue ?? null;
    });
    updatedData.CustomFilterVariables = customFilterVariables;
  }
  return updatedData;
};

// Payload Creation for drilldown and export done in drilldown report, Returns the complete object as ReportFetchCriteria
// @Input: Take all the redux stores value and create the required payload.
export const getDrilldownReportDataLoad = (reportId: string, autodrilldownData: ReportReduxModel.IAutoDrilldownData,
  filterConfig: Array<ReportResponseModel.IFilterConfig>, columns: Array<ReportResponseModel.IColumn>,
  activeDimensions: Array<ReportReduxModel.IDimension>, measures: Array<ReportResponseModel.IColumn>,
  activeFilters: ObjModel.ObjGeneric<FilterModel.IFilterResponse>, dynamicDimensions:Array<ReportResponseModel.IColumn>,
  entityGroupStore: Record<string, string>, entityGroupOptionsMap: Record<string, IDimensionMetaField[]>, customHierarchyDrilldownData?: ReportReduxModel.ICustomHierarchyDrilldownData, filterMetaData?: IFiltersMetaData, customHierarchies?: ICustomHierarchy[], travelBackToMainReportFromCustomHierarchyReport?: boolean, drilldownStatePayload?: ObjModel.Obj | null, subBuilders?: IBuilder[], customFilterDefinitions?: ReportResponseModel.ICustomFilterDefinition[]): ReportFetchCriteria => {
  const selectedRows: SelectedRow[] = [];

  let activeDimensionsUpdated: IDimension[] = activeDimensions ?? [];
  let columnsUpdated: IColumn[] = columns ?? [];
  if (isValidObjectAndNotEmpty(customHierarchyDrilldownData)) {
    // Transform IColumn[] to IDimension[] using the conversion utility
    activeDimensionsUpdated = customHierarchyDrilldownData?.columns
      ?.filter((col) => col?.Props?.Dimension) // Only include columns with Dimension property
      .map((col) => convertColumnToDimension(col)) ?? [];
    columnsUpdated = customHierarchyDrilldownData?.columns as IColumn[];
  }
  if (isValidArrayAndNotEmpty(activeDimensions)) {
    activeDimensionsUpdated?.map((dim, index) => {
      if (dim.Applied !== DimensionStatus.NotApplied) {
        let considerDim = false;
        const selectedRowData = autodrilldownData?.selectedRowData;
        const isColumnTotalSelected = autodrilldownData?.clickedOnColumnTotal;
        const isRowTotalSelected = autodrilldownData?.clickedOnRowTotal;
        const isSubTotalClicked = autodrilldownData?.clickedOnSubTotal;
        const isGroupedSubTotalClicked = autodrilldownData?.clickedOnGroupedSubTotal;
        const column = isValidArrayAndNotEmpty(columnsUpdated) ? columnsUpdated?.find((col) => (col.Id === dim.Id)) : null;
        if (isRowTotalSelected && dim?.DimensionMode !== DimensionMode.ColumnGroup) considerDim = true;
        else if (!isRowTotalSelected && isColumnTotalSelected && dim?.DimensionMode === DimensionMode.ColumnGroup) considerDim = true;
        else if (!isRowTotalSelected && !isColumnTotalSelected) considerDim = true;

        if (isSubTotalClicked && index > 0 && dim?.DimensionMode === DimensionMode.RowGroup) considerDim = false;
        if (isGroupedSubTotalClicked && index > 1 && dim?.DimensionMode === DimensionMode.RowGroup) considerDim = false;
        // removing the hidden column from selected row in drilldown
        if (column && dim?.DimensionMode === DimensionMode.RowGroup && isColumnUserHidden(column, columnsUpdated)) considerDim = false;
        if (considerDim) {
          const name = dim.BuilderConfig && dim.BuilderConfig.IsDynamicField ? dim.BuilderConfig.Alias : dim.Name;
          const entity = dim.BuilderConfig ? dim?.BuilderConfig?.Entity : '';
          const binningType = column?.Props?.Dimension?.DimensionProp?.Props?.BinningType;
          const typeConvertedValue = TypeConversionStringToInt(column?.BuilderConfig?.DataType, selectedRowData[dim.Id], binningType);
          const selectedRow: SelectedRow = {
            Alias: name,
            Entity: entity,
            Value: typeConvertedValue,
            IsMasked: column?.Props?.IsMasked,
            Id: dim.Id,
          };
          selectedRows.push(selectedRow);
        }
      }
      return null;
    });
  }

  const updatedMeasures = getUpdatedMeasures(measures, columnsUpdated);

  const groupedDrilldownFields : IColumn[] = isValidArrayAndNotEmpty(autodrilldownData?.drilldownFields) ? autodrilldownData?.drilldownFields?.filter((dim) => dim?.Props?.Dimension?.Applied === DimensionStatus.Applied) : null;
  const fetchDrillDownRequest = {
    ReportId: reportId,
    Filters: {},
    Page: autodrilldownData.page,
    FiltersMetadata: {},
    Sort: autodrilldownData.sort,
    Columns: getColumns(columnsUpdated, activeDimensionsUpdated, updatedMeasures, dynamicDimensions, entityGroupStore, entityGroupOptionsMap),
    SelectedMeasure: autodrilldownData.selectedMeasure,
    SelectedRow: autodrilldownData.rowPinnedClicked ? [] : selectedRows,
    DrilldownColumns: groupedDrilldownFields,
    AppliedHierarchy: [],
  } as ReportFetchCriteria;

  return getUpdatedReportData(filterConfig, activeFilters, fetchDrillDownRequest, filterMetaData, customHierarchies, customHierarchyDrilldownData, travelBackToMainReportFromCustomHierarchyReport, drilldownStatePayload, subBuilders, customFilterDefinitions);
};

// SIERA-10682: GroupConcat and GroupConcatDistinct do not support drill-down
export const isDrilldownPossible = (expr?: IExpression) => (
  expr && expr.Type && isAgg(expr) && !isGroupConcat(expr) && !isGroupConcatDistinct(expr)
);

export const setDrilldownEnableFlag = (measure: IColumn) => {
  const updatedMeasureInfo = { ...measure };
  const { Props } = updatedMeasureInfo;
  if (Props?.Measure?.IsPreDefined) {
    updatedMeasureInfo.Props.DrilldownEnabled = Object.prototype.hasOwnProperty.call(Props, 'DrilldownEnabled') ? updatedMeasureInfo?.Props?.DrilldownEnabled : measure?.Props?.AllowDrillDown;
  } else {
    const enableDrilldown = isDrilldownPossible(measure?.Props?.Measure?.Expression);
    if (enableDrilldown) {
      updatedMeasureInfo.Props.DrilldownEnabled = Object.prototype.hasOwnProperty.call(Props, 'DrilldownEnabled') ? updatedMeasureInfo?.Props?.DrilldownEnabled : enableDrilldown;
    } else {
      updatedMeasureInfo.Props.DrilldownEnabled = false;
    }
  }
  return updatedMeasureInfo;
};

export const getUniqIdentiferForDrilldownCols = (dim: IDimension | IColumn) => `${dim?.BuilderConfig?.Entity}:${dim?.BuilderConfig?.JoinID ? `${dim?.BuilderConfig?.JoinID}:` : ''}${dim?.BuilderConfig?.Alias}`;

export const getDefaultEntity = (reportConfig : IReportConfig) => {
  const defaultNamespace = reportConfig?.Builder[0]?.Namespace;
  const namespaceTypes = reportConfig?.ConfigMetadata?.namespaceTypes;
  if (!defaultNamespace || !namespaceTypes) return '';
  return namespaceTypes[defaultNamespace as keyof object] || '';
};

export const subQuerySortForMeasures = (
  sortState: ISorting[], appliedDimensions: IDimension[], appliedMeasures: IColumn[],
  columnType: draggableItemType,
  columnName: string,
  order: SortingOrderTypes | null, // null implies removal of sort
  multiSort: boolean,
  Id: string,
  isReportListType: boolean, // true if report is of List Report type
): [ISorting[], boolean] => {
  if (!columnExists(Id, columnType, appliedDimensions, appliedMeasures, [])) { return [sortState, false]; }
  const newSortState = [...sortState];
  const sortIndex = newSortState.findIndex((s) => s.Id === Id);
  const newOrder = disableInactiveSorting(newSortState, appliedDimensions, columnType, order, Id);

  if (!(newOrder || sortIndex >= 0)) { // removing an un-applied sort is not possible
    return [newSortState, false];
  }
  const deleteStart = deleteNonMandatory(newSortState, appliedDimensions, multiSort, isReportListType); // delete non-mandatory sorts if not multi sorting

  if (newOrder && sortIndex >= 0 && sortIndex < deleteStart && sortIndex < newSortState?.length) { // replaces applied or non-deleted sort objects
    newSortState[sortIndex].Field = columnName;
    newSortState[sortIndex].Direction = newOrder;
    newSortState[sortIndex].Id = Id;
  } else if (!newOrder) { // removes the sort object
    newSortState.splice(sortIndex, 1);
  } else if (sortIndex < 0 || sortIndex >= deleteStart) { // pushes newly applied or just-deleted sort objects to the list
    newSortState.push({
      Field: columnName,
      Direction: newOrder,
      Id,
    });
  }

  return [rebuildSortState(newSortState, appliedDimensions, appliedMeasures, [], !multiSort, false, isReportListType), true];
};

export const getEntitiesIcons = (entity: string) => {
  switch (entity) {
    case PreDefinedFieldEntityType: return {
      icon: PreDefinedIcon,
      color: 'var(--purpleColor)',
    };
    case EntitiesTypes.Leads: return {
      icon: LeadIcon,
      color: 'var(--avtarColor)',
    };
    case EntitiesTypes.Activities: return {
      icon: ActivityIcon,
      color: 'var(--expandListButtonHoverColor)',
    };
    case EntitiesTypes.Opportunities: return {
      icon: OpportunityIcon,
      color: 'var(--opportunityColor)',
    };
    case EntitiesTypes.Tasks: return {
      icon: TaskIcon,
      color: 'var(--taskColor)',
    };
    case EntitiesTypes.Users: return {
      icon: UserIcon,
      color: 'var(--userColor)',
    };
    case EntitiesTypes.Customs: return {
      icon: CustomExpressionIcon,
      color: 'var(--customEntiyColor)',
    };
    case EntitiesTypes.Tickets: return {
      icon: TicketEntity,
      color: 'var(--ticketEntityColor)',
    };
    case EntitiesTypes.TicketActivities: return {
      icon: TicketActivityEntity,
      color: 'var(--ticketActivityEntity)',
    };
    case EntitiesTypes.Accounts: return {
      icon: AccountsEntityIcon,
      color: 'var(--accountsEntity)',
    };
    case EntitiesTypes.AccountsActivities: return {
      icon: AccountsActivityEntityIcon,
      color: 'var(--accountsActivityEntity)',
    };
    case EntitiesTypes.WebContents: return {
      icon: WebContentEntityIcon,
      color: 'var(--webContentEntity)',
    };
    case EntitiesTypes.LeadSystemAudits: return {
      icon: LeadSystemAuditIcon,
      color: 'var(--avtarColor)',
    };
    case EntitiesTypes.Metadata: return {
      icon: MetaDataIcon,
      color: 'var(--metaDataColor)',
    };
    case EntitiesTypes.System: return {
      icon: SystemDataIcon,
      color: 'var(--systemDataColor)',
    };
    case EntitiesTypes.DistributionEngine: return {
      icon: DistributionEngineIcon,
      color: 'var(--distributionEngineColor)',
    };
    case EntitiesTypes.FieldSales: return {
      icon: FieldSalesIcon,
      color: 'var(--fieldSalesColor)',
    };
    case EntitiesTypes.UnionTable: return {
      icon: UnionTableIcon,
      color: 'var(--purpleColor)',
    };
    default: return {
      icon: CustomEntityIcon,
      color: 'var(--customIconColor)',
    };
  }
};

/**
 * Generates dimension metafields payload for saving report configuration.
 * Processes entity group store entries and creates meta field entries.
 *
 * @param dimennsionMetaFields - Existing dimension metafields
 * @param dimensions - List of all dimensions
 * @param entityGroupStore - Optional entity group store
 * @param entityGroupOptionsMap - Optional entity group options map
 * @returns Updated array of dimension metafields
 */
export const getSavePayloadEntityGroupDimensionMetafields = (
  dimennsionMetaFields: IDimensionsMetaFields[],
  dimensions: IDimension[],
  entityGroupStore?: Record<string, string>,
  entityGroupOptionsMap?: Record<string, IDimensionMetaField[]>,
): IDimensionsMetaFields[] => {
  const updatedDimenstionMetaFields = dimennsionMetaFields?.slice() ?? [];
  if (isValidObjectAndNotEmpty(entityGroupStore) && isValidObjectAndNotEmpty(entityGroupOptionsMap)) {
    // Process entity group store entries
    Object.entries(entityGroupStore).forEach(([dimensionId, optionId]) => {
      try {
        // Find the dimension - skip if not found
        const dimension = dimensions.find((dim) => dim.Id === dimensionId);
        if (!dimension) return;

        // Get options for this dimension - skip if none
        const options = entityGroupOptionsMap[dimensionId];
        if (!isValidArrayAndNotEmpty(options)) return;

        // Find the selected option - skip if not found
        const selectedOption = options.find((opt) => opt.Id === optionId);
        if (!selectedOption?.DimensionMetaField) return;

        // Create meta field entry
        const metaFieldEntry: IDimensionsMetaFields = {
          Id: dimension.Id,
          Type: selectedOption.DimensionMetaField.Type,
          Dimension: selectedOption.DimensionMetaField.Dimension,
          QueryId: '',
          Fields: [{
            SchemaName: selectedOption.DimensionMetaField.Fields[0]?.SchemaName || '',
            Alias: selectedOption.DimensionMetaField.Fields[0]?.Alias || '',
            Id: dimension.Props?.ReferTo || '',
          }],
        };
        const dimensionIndex = updatedDimenstionMetaFields.findIndex((dim: IDimensionsMetaFields) => dim.Id === dimensionId);
        if (dimensionIndex >= 0) {
          updatedDimenstionMetaFields[dimensionIndex] = metaFieldEntry;
        } else {
          updatedDimenstionMetaFields.push(metaFieldEntry);
        }
      } catch (error) {
        console.error(`Error processing dimension ${dimensionId}:`, error);
      }
    });
  }
  return updatedDimenstionMetaFields;
};

/**
 * Determines the destination dimension status based on source and destination dimensions.
 * Handles mandatory status propagation.
 *
 * @param desDimension - Destination dimension
 * @param dimension - Source dimension
 * @returns Calculated dimension status
 */
export const getDestinationDimensionStatus = (desDimension: IDimension, dimension: IDimension): DimensionStatus => {
  let desDimStatus;
  if (desDimension.Applied === DimensionStatus.Mandatory) {
    desDimStatus = DimensionStatus.Mandatory;
    // and if source dimension is mandatory applied , then it should be applied
  } else if (dimension.Applied === DimensionStatus.Mandatory) {
    desDimStatus = DimensionStatus.Applied;
    // if both are mandatory or for any other case status should be swapped.
  } else {
    desDimStatus = dimension.Applied;
  }
  return desDimStatus;
};

export const TypeConversionStringToInt = (dataType: string, value: any, binningType?: string) => {
  if (isNumericDataType(dataType) && (value === null || value === '' || value === DimensionNumericNoValue)) {
    return null;
  }

  if (value === DimensionNoValue) {
    return '';
  }
  if (dataType === ColumnDataTypes.String) {
    return value;
  }
  if (dataType === ColumnDataTypes.StringArray) {
    if (typeof value === 'number' && value === 0) {
      return '';
    }
    return value;
  }

  const num = Number(value);
  if (!Number.isNaN(num) && value !== '') {
    return isDateTimeBinningEmptyString(dataType, value, binningType) ? '' : num;
  }
  // If conversion fails, return the original string
  return value;
};

export const getUniqIdentiferForDrilldownColsWithId = (dim: IDimension | IColumn) => `${getUniqIdentiferForDrilldownCols(dim)}:${dim.Id}`;

/**
 * Checks if a data type is a numeric type (Int, Int64, or Decimal).
 * @param dataType - The data type to check
 * @returns true if the data type is Int, Int64, or Decimal, false otherwise
 */
export const isNumericDataType = (dataType: string): boolean => dataType === ColumnDataTypes.Int || dataType === ColumnDataTypes.Int64 || dataType === ColumnDataTypes.Decimal;

const isDateTimeBinningEmptyString = (dataType: string, value: any, binningType?: string): boolean => {
  if (dataType !== ColumnDataTypes.DateTime || !binningType || value !== 0) {
    return false;
  }
  return ZERO_TO_EMPTY_STRING_BINNING_TYPES.has(binningType as DateBinningFormatters);
};

/**
 * Updates BuilderConfig.DisplayName for all columns in the provided array.
 * Sets DisplayName to column.Name for each column that has a BuilderConfig.
 * @param columns - Array of columns to update
 */
export const updateColumnsDisplayName = (columns?: ReportResponseModel.IColumn[] | null): void => {
  if (isValidArrayAndNotEmpty(columns)) {
    columns.forEach((column: ReportResponseModel.IColumn) => {
      if (column.BuilderConfig) {
        column.BuilderConfig.DisplayName = column.Name;
      }
    });
  }
};

// entity accent
export const getFieldEntityColor = (field: IColumn) => {
  let entity = field?.BuilderConfig?.NamespaceEntity;
  if (isMeasureCustom(field)) entity = FieldEntitiesType.Custom;
  else if (isMeasurePredefined(field)) entity = PreDefinedFieldEntityType;
  return field?.Props?.QueryId || field?.BuilderConfig?.EntityName
    ? FieldEntities[FieldEntitiesType.Query].color
    : FieldEntities[entity as keyof typeof FieldEntities]?.color;
};

/**
 * This function returns a filter id suffixed with the query id of the filter.
 * When sub builder filters are saved in the report, their Ids get appended with the query id perhaps to maintain uniqueness.
 * @param filterId
 * @param queryId
 * @returns filter id resolved with query id
 */
export const resolveFilterIDInCaseOfQueryIdPresent = (filterId: string, queryId?: string) => {
  let resolvedFilterId = filterId;
  if (queryId) {
    resolvedFilterId = filterId?.endsWith(queryId) ? filterId : `${filterId}_${queryId}`;
  }
  return resolvedFilterId;
};
